import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { MAP_STYLES, MapStyle, DEFAULT_MAP_STYLE } from '../lib/mapConfig';

// Fix for default marker icons in Leaflet with webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

export interface MapMarker {
  lat: number;
  lng: number;
  title?: string;
  color?: string;
  onClick?: () => void;
  count?: number;
}

export type MarkerStyle = 'circle' | 'circle-border' | 'rounded-square' | 'dot-ring' | 'pin' | 'pin-no-shadow';

interface MapProps {
  center: [number, number];
  zoom: number;
  markers?: MapMarker[];
  onMapClick?: (lat: number, lng: number) => void;
  onBoundsChange?: (bounds: { north: number; south: number; east: number; west: number }) => void;
  className?: string;
  dropPinMode?: boolean;
  mapStyle?: MapStyle;
  markerStyle?: MarkerStyle;
}

export default function Map({ center, zoom, markers = [], onMapClick, onBoundsChange, className = '', dropPinMode = false, mapStyle = DEFAULT_MAP_STYLE, markerStyle = 'circle' }: MapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markerClusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    console.log('Initializing map...', mapContainerRef.current.offsetHeight, mapContainerRef.current.offsetWidth);

    const map = L.map(mapContainerRef.current, { attributionControl: false }).setView(center, zoom);

    const styleConfig = MAP_STYLES[mapStyle];
    const tileLayer = L.tileLayer(styleConfig.url, {
      maxZoom: styleConfig.maxZoom,
      attribution: styleConfig.attribution,
      tileSize: styleConfig.tileSize,
      zoomOffset: styleConfig.zoomOffset,
    }).addTo(map);

    tileLayerRef.current = tileLayer;
    mapRef.current = map;
    
    // Report initial bounds
    if (onBoundsChange) {
      const bounds = map.getBounds();
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
    }

    // Listen for map movements (zoom, pan)
    map.on('moveend', () => {
      if (onBoundsChange) {
        const bounds = map.getBounds();
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      }
    });
    
    // Create marker cluster group with custom styling
    const clusterGroup = L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="
            background: #3B82F6;
            color: white;
            border-radius: 50%;
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 16px;
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
            border: 3px solid white;
            cursor: pointer;
          ">${count}</div>`,
          className: 'custom-cluster-icon',
          iconSize: L.point(48, 48),
        });
      },
      spiderfyOnMaxZoom: true, // Enable spiderfy when can't zoom further
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true, // Enable default zoom behavior
      disableClusteringAtZoom: 19, // Increase to allow more zoom
      maxClusterRadius: 50, // Reduce to make tighter clusters
      spiderfyDistanceMultiplier: 1.5, // Control spider leg length
      animate: true,
      animateAddingMarkers: false,
    });

    // Custom zoom behavior on cluster click
    clusterGroup.on('clusterclick', (a: any) => {
      const cluster = a.layer;
      const bounds = cluster.getBounds();
      map.fitBounds(bounds, {
        padding: [80, 80], // Add padding so pins aren't at the edge
        maxZoom: 18, // Allow closer zoom before spiderfying
        animate: true,
        duration: 0.8, // Smooth animation (0.8 seconds)
      });
    });

    clusterGroup.addTo(map);
    markerClusterGroupRef.current = clusterGroup;

    if (onMapClick) {
      map.on('click', (e) => {
        console.log('Map clicked:', e.latlng.lat, e.latlng.lng);
        onMapClick(e.latlng.lat, e.latlng.lng);
      });
    }

    // Force resize multiple times to ensure tiles load
    const resizeMap = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        console.log('Map invalidated');
      }
    };

    setTimeout(resizeMap, 50);
    setTimeout(resizeMap, 200);
    setTimeout(resizeMap, 500);

    // Add window resize listener
    window.addEventListener('resize', resizeMap);

    return () => {
      window.removeEventListener('resize', resizeMap);
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      markerClusterGroupRef.current = null;
    };
  }, [onMapClick]);

  // Update map style when it changes
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;

    const styleConfig = MAP_STYLES[mapStyle];
    
    // Remove old tile layer
    mapRef.current.removeLayer(tileLayerRef.current);
    
    // Add new tile layer
    const newTileLayer = L.tileLayer(styleConfig.url, {
      maxZoom: styleConfig.maxZoom,
      attribution: styleConfig.attribution,
      tileSize: styleConfig.tileSize,
      zoomOffset: styleConfig.zoomOffset,
    }).addTo(mapRef.current);
    
    tileLayerRef.current = newTileLayer;
  }, [mapStyle]);

  // Update center and zoom
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setView(center, zoom, { animate: true, duration: 0.5 });
      // Invalidate size when center/zoom changes to ensure proper rendering
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 50);
    }
  }, [center, zoom]);

  // Update markers
  useEffect(() => {
    if (!markerClusterGroupRef.current) return;

    markerClusterGroupRef.current.clearLayers();

    markers.forEach((marker) => {
      let markerIcon: L.Icon | L.DivIcon = DefaultIcon;
      
      // Create custom colored pin markers
      if (marker.color) {
        const markerHtml = `
          <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 4px 12px rgba(0,0,0,0.4)); cursor: pointer;">
            <path d="M18 0C8.059 0 0 8.059 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.059 27.941 0 18 0z" 
                  fill="${marker.color}" 
                  stroke="#ffffff" 
                  stroke-width="2.5"/>
            <circle cx="18" cy="18" r="6" fill="#ffffff" opacity="0.9"/>
          </svg>
        `;
        
        markerIcon = L.divIcon({
          className: 'custom-marker',
          html: markerHtml,
          iconSize: [36, 48],
          iconAnchor: [18, 48],
        });
      }
      
      const leafletMarker = L.marker([marker.lat, marker.lng], { 
        icon: markerIcon
      });
      
      if (marker.title) {
        leafletMarker.bindTooltip(marker.title);
      }
      
      if (marker.onClick) {
        leafletMarker.on('click', marker.onClick);
      }

      markerClusterGroupRef.current!.addLayer(leafletMarker);
    });
  }, [markers]);

  useEffect(() => {
    if (!mapContainerRef.current || !mapRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    });

    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={mapContainerRef}
      className={`${dropPinMode ? 'cursor-crosshair' : ''}`}
      style={{ width: '100%', height: '100%', minHeight: '400px' }}
    />
  );
}

